import { formatFileSize } from '@/lib/base/file-upload/file-utils';

export interface FileSizeValueLabelProps {
  value: string | number | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for file_size field type values.
 * Formats raw byte counts into human-readable sizes (KB, MB, GB).
 *
 * @example
 * <FileSizeValueLabel value="1048576" />
 * // Renders: "1 MB"
 *
 * <FileSizeValueLabel value="512" />
 * // Renders: "512 B"
 */
export const FileSizeValueLabel: React.FC<FileSizeValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? String(value) : undefined}
      >
        {String(value)}
      </span>
    );
  }

  const formatted = formatFileSize(numValue);
  const tooltip = `${numValue} bytes`;

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={tooltip}
    >
      {formatted}
    </span>
  );
};
